/**
 * Settings → Email (§3.2: "connection status, mailbox address, last sync time,
 * and a Disconnect button").
 *
 * Design choice worth defending: **the mailbox address is shown in full, and the
 * run history is shown by default rather than hidden behind a disclosure.**
 * Sync is the one part of MyDeck that acts on its own, unattended, between
 * visits. If it quietly stops — a revoked app password, a bank changing its
 * sending address — nothing else on screen would look wrong; statements would
 * simply stop appearing, and a missing thing is invisible. So the audit trail
 * is the primary content of this panel, not a detail view. That is §7.7 given a
 * surface.
 *
 * Disconnecting is deliberately *not* a button here. Credentials live in .env
 * (§14) — they are never writable through the API, so a Disconnect button would
 * either lie about what it did or need an endpoint that writes secrets. The
 * panel tells the user exactly which line to remove instead.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  // Aliased: the bare name collides with the DOM's global `History` type.
  History as HistoryIcon,
  Loader2,
  PlugZap,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import { Badge, Button, Panel } from "@/components/ui/primitives";
import { syncApi, type SyncRun, type SyncStatus } from "@/lib/api/client";
import { formatDateTime, formatRelativeTime } from "@/lib/format";

import { SyncFrequency } from "./SyncFrequency";

export function EmailPanel() {
  const qc = useQueryClient();

  const {
    data: status,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["sync", "status"],
    queryFn: syncApi.status,
    // A running sync should show up without a manual refresh, but this endpoint
    // must never dial the mailbox — it reads local rows only.
    refetchInterval: 60_000,
  });

  const { data: runs } = useQuery({
    queryKey: ["sync", "runs"],
    queryFn: () => syncApi.runs(10),
    enabled: Boolean(status?.configured),
  });

  const test = useMutation({
    mutationFn: syncApi.test,
    onSuccess: (result) => {
      qc.setQueryData(["sync", "status"], result);
      if (result.connection_ok) toast.success(result.detail ?? "Connected.");
      else toast.error(result.detail ?? "Could not connect.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const run = useMutation({
    // Keyed so the top-bar SyncPill can show the spinner too (useIsMutating).
    mutationKey: ["sync", "run"],
    mutationFn: (full: boolean) => syncApi.run(full),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["sync"] });
      qc.invalidateQueries({ queryKey: ["statements"] });
      if (result.ok) toast.success(result.message);
      else toast.error(result.error ?? "Sync failed.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <EmailPanelSkeleton />;

  if (isError) {
    return (
      <Panel className="p-5">
        <PanelHeading />
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-overdue/25 bg-overdue/10 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-overdue" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-ink">Couldn't read the sync status.</p>
            <p className="mt-0.5 text-xs text-muted">{(error as Error)?.message}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </Panel>
    );
  }

  if (!status) return null;

  return (
    <Panel className="overflow-hidden">
      <div className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <PanelHeading />
          <ConnectionBadge status={status} />
        </div>

        {status.mode === "disabled" ? (
          <DisabledGuidance detail={status.detail} />
        ) : (
          <>
            <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
              <Detail label="Mailbox">
                <span className="break-all font-mono text-sm text-ink">
                  {status.mailbox ?? "—"}
                </span>
              </Detail>
              <Detail label="Connection">
                <span className="text-sm text-ink">
                  {status.mode === "imap" ? "IMAP" : "Gmail API"}, read-only
                </span>
              </Detail>
              <Detail label="Last sync">
                <LastSync run={status.last_run} />
              </Detail>
            </dl>

            <SyncFrequency status={status} />

            {status.detail && !status.configured && (
              <p className="mt-4 rounded-lg border border-warn/25 bg-warn/10 p-3 text-xs text-warn">
                {status.detail}
              </p>
            )}

            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => run.mutate(false)}
                disabled={run.isPending || !status.configured}
              >
                {run.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <RefreshCw className="h-4 w-4" aria-hidden />
                )}
                {run.isPending ? "Syncing…" : "Sync now"}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => run.mutate(true)}
                disabled={run.isPending || !status.configured}
                title="Retry every parked email. Use this after adding a PDF password, a card, or a new bank parser. Mail from a newly added sender is picked up by the next sync on its own."
              >
                <HistoryIcon className="h-4 w-4" aria-hidden />
                Rescan everything
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => test.mutate()}
                disabled={test.isPending}
              >
                {test.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <PlugZap className="h-4 w-4" aria-hidden />
                )}
                Test connection
              </Button>
            </div>

            <p className="mt-3 max-w-prose text-xs text-faint">
              Mail from a sender MyDeck doesn't recognise is <em>parked</em>, not consumed —
              add the sender below and the next sync picks it up. After adding a PDF password
              or a card, use <strong className="text-muted">Rescan everything</strong>{" "}
              to retry what was waiting on it. To disconnect, clear{" "}
              <code className="font-mono text-faint">EMAIL_MODE</code> in{" "}
              <code className="font-mono text-faint">.env</code> and restart; credentials are
              never writable through the app.
            </p>
          </>
        )}
      </div>

      {status.configured && <RunHistory runs={runs} />}
    </Panel>
  );
}

function PanelHeading() {
  return (
    <div>
      <h2 className="font-display text-base font-medium text-ink">Email</h2>
      <p className="mt-1 max-w-prose text-sm text-muted">
        A dedicated inbox that your personal mail forwards card statements and alerts
        into. MyDeck never touches your personal mailbox.
      </p>
    </div>
  );
}

function ConnectionBadge({ status }: { status: SyncStatus }) {
  if (status.mode === "disabled") return <Badge tone="neutral">Not connected</Badge>;
  if (!status.configured) return <Badge tone="overdue">Needs attention</Badge>;
  if (status.connection_ok === false) return <Badge tone="overdue">Connection failed</Badge>;
  if (status.last_run && !status.last_run.ok) return <Badge tone="warn">Last sync failed</Badge>;
  return <Badge tone="paid">Connected</Badge>;
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="label-micro">{label}</dt>
      <dd className="mt-1 min-w-0">{children}</dd>
    </div>
  );
}

function LastSync({ run }: { run: SyncRun | null }) {
  if (!run) return <span className="text-sm text-faint">Never</span>;
  const when = run.finished_at ?? run.started_at;
  return (
    <span className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-sm text-ink" title={formatDateTime(when)}>
        {formatRelativeTime(when)}
      </span>
      {!run.ok && (
        <span className="text-xs text-overdue">failed</span>
      )}
    </span>
  );
}

function DisabledGuidance({ detail }: { detail: string | null }) {
  return (
    <div className="mt-4 space-y-3">
      <p className="text-sm text-muted">
        {detail ??
          "Email sync is off. Statements can still be uploaded by hand on the Statements page."}
      </p>
      <ol className="space-y-1.5 text-xs text-faint">
        <li>
          1. Create a dedicated mail account and forward your banks' mail to it.
        </li>
        <li>
          2. Set <code className="font-mono">EMAIL_MODE</code>,{" "}
          <code className="font-mono">IMAP_USER</code> and{" "}
          <code className="font-mono">IMAP_APP_PASSWORD</code> in{" "}
          <code className="font-mono">.env</code>.
        </li>
        <li>3. Restart MyDeck. This panel will show the connection.</li>
      </ol>
      <p className="text-xs text-faint">
        Full walkthrough in <code className="font-mono">docs/SETUP-EMAIL.md</code>.
      </p>
    </div>
  );
}

function RunHistory({ runs }: { runs: SyncRun[] | undefined }) {
  if (!runs) {
    return (
      <div className="space-y-2 border-t border-hairline p-5">
        <div className="skeleton h-4 w-24" />
        <div className="skeleton h-9 w-full" />
        <div className="skeleton h-9 w-full" />
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="border-t border-hairline bg-base/40 px-5 py-4">
        <p className="label-micro">Recent syncs</p>
        <p className="mt-2 text-sm text-muted">
          No syncs yet. Use <strong className="font-medium text-ink">Sync now</strong> to
          run the first one.
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-hairline bg-base/40">
      <p className="label-micro px-5 pb-2 pt-4">Recent syncs</p>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[34rem] text-sm">
          <caption className="sr-only">
            Recent email sync runs, newest first, with counts and any errors.
          </caption>
          <thead>
            <tr className="border-y border-hairline text-left">
              <th scope="col" className="label-micro px-5 py-2 font-normal">
                When
              </th>
              <th scope="col" className="label-micro px-3 py-2 font-normal">
                Trigger
              </th>
              <th scope="col" className="label-micro px-3 py-2 text-right font-normal">
                Read
              </th>
              <th scope="col" className="label-micro px-3 py-2 text-right font-normal">
                Added
              </th>
              <th scope="col" className="label-micro px-3 py-2 text-right font-normal">
                To review
              </th>
              <th scope="col" className="label-micro px-5 py-2 font-normal">
                Result
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {runs.map((r) => (
              <tr key={r.id} className="align-top">
                <td
                  className="whitespace-nowrap px-5 py-2.5 text-muted"
                  title={formatDateTime(r.started_at)}
                >
                  {formatRelativeTime(r.started_at)}
                </td>
                <td className="px-3 py-2.5 text-muted">{r.source}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                  {r.emails_seen}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                  {r.statements_ingested || "—"}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-ink">
                  {r.review_items_created || "—"}
                </td>
                <td className="px-5 py-2.5">
                  {r.ok ? (
                    <span className="inline-flex items-center gap-1.5 text-paid">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      OK
                    </span>
                  ) : (
                    <span className="inline-flex items-start gap-1.5 text-overdue">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span className="max-w-[22rem] text-xs">{r.error ?? "Failed"}</span>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmailPanelSkeleton() {
  return (
    <Panel className="p-5" aria-busy="true">
      <div className="space-y-2">
        <div className="skeleton h-5 w-20" />
        <div className="skeleton h-4 w-full max-w-md" />
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div className="skeleton h-10 w-full" />
        <div className="skeleton h-10 w-full" />
        <div className="skeleton h-10 w-full" />
        <div className="skeleton h-10 w-full" />
      </div>
      <div className="mt-5 flex gap-2">
        <div className="skeleton h-8 w-28" />
        <div className="skeleton h-8 w-36" />
      </div>
    </Panel>
  );
}

